import {
    addGenericShopBasketItem,
    loadGenericShopBasket,
} from "@/abilities/shop/features/generic/api/generic-shop-basket";
import { loadGenericShopCheckoutUrl } from "@/abilities/shop/features/generic/api/load-generic-shop-checkout";
import {
    buildShopItemInfoInput,
    loadGenericShopItemInfo,
} from "@/abilities/shop/features/generic/api/load-generic-shop-item-info";
import { useShopStatusStore } from "@/abilities/shop/features/shop-core/store/shop-status.store";
import { ShopCore } from "@/abilities/shop/types/shop-core";
import { ShopArticleContext } from "@/shared/environment/widgets/widget-context";

/**
 * The generic shop adapter (QIT-7074) — one provider-agnostic implementation of
 * `ShopCore.IShopActions` on top of the Generic Shop Interface, replacing the
 * per-provider adapters the epic retires (QIT-6603).
 *
 * What makes it different from FDI/Salesforce/SAP:
 *
 * - **It builds no client.** Every operation goes through the InfoTwin consumer
 *   graph via the `api/` loaders. No browser→external-shop call, no shop token
 *   in the SPA, and therefore no `getShopClient()`.
 * - **It holds no identifiers.** No `basketId`, no `cartId` — the shop service
 *   resolves the basket from the authenticated session, so `getCartId()` and
 *   `getBasketIdForCurrentUser()` are deliberately not implemented.
 * - **It applies no shop rules.** Price, stock, price-on-request and
 *   orderability are read from `shopItemInfo` and rendered as given (QIT-7073).
 */
export function useGenericShop(): ShopCore.IShopActions {
    /**
     * True after a failed basket read, until a read succeeds again. Keeps the
     * cart badge from asking for the session every 10 seconds.
     */
    let basketReadFailed = false;

    /**
     * Call this after a shop call failed. It asks the shop again whether the
     * user is logged in, and starts the login if not.
     *
     * The error itself does not tell: when the FDI middleware restarts, it
     * forgets all logins and answers every call with a generic server error.
     * `shopSession` then returns `NOT_AUTHENTICATED` (QIT-7382).
     *
     * @returns true when the browser is on its way to the login page
     */
    async function startLoginIfSessionLost(): Promise<boolean> {
        const store = useShopStatusStore();
        await store.refreshGenericShopSession();
        return store.startGenericShopLogin();
    }

    /**
     * Reads price and stock for one article.
     *
     * The loader behind this is set-shaped (`loadGenericShopItemInfos`), so the
     * widget's one-article-at-a-time calls can become a single batch request
     * once the backend has the batch field, without touching this adapter.
     */
    async function getShopItemInfoForArticle(
        articleId: string,
        context?: ShopArticleContext
    ): Promise<ShopCore.WidgetItem | null> {
        return loadGenericShopItemInfo(buildShopItemInfoInput(articleId, context));
    }

    /**
     * Adds an article to the basket of the authenticated user.
     *
     * The article lookup is built with the same helper the item-info read uses,
     * so an article cannot be priced against one asset/product and added
     * against another.
     *
     * If adding fails, the promise rejects and the widget shows its error
     * message. Exception: if the user is no longer logged in, the login starts
     * instead.
     */
    async function addPartToShopBasket(
        articleId: string,
        quantity: number,
        context?: ShopArticleContext
    ): Promise<void> {
        try {
            await addGenericShopBasketItem({ ...buildShopItemInfoInput(articleId, context), amount: quantity });
        } catch (error) {
            if (await startLoginIfSessionLost()) {
                // The browser is on its way to the login page. A promise that
                // never settles keeps the widget from showing "could not be
                // added" before the page changes.
                return new Promise<never>(() => {});
            }
            throw error;
        }
    }

    /**
     * Reads the current basket, wrapped as `{ basket }`.
     *
     * The wrapper is what `shopping-cart-menu.vue` already reads
     * (`data.basket?.items?.[].amount`), so the cart menu and its badge drive
     * this adapter unchanged — a requirement of the ticket. The badge sums the
     * amounts client-side because `ShopBasket.itemCount` does not exist yet
     * (gap 7 of the QIT-6923 asks).
     */
    async function getBasket() {
        const basket = await loadGenericShopBasket();
        if (basket) {
            basketReadFailed = false;
            return { basket };
        }

        // The cart badge reads in the background, so a failed read never starts
        // the login: that would take the user off the page while they read.
        // It only updates the stored session, after the first failed read in a
        // row and not every 10 seconds.
        if (!basketReadFailed) {
            basketReadFailed = true;
            await useShopStatusStore().refreshGenericShopSession();
        }
        return undefined;
    }

    /**
     * Sends a price request for an article with "price on request" (QIT-7369).
     *
     * The generic contract has no price-request operation. The shop's
     * middleware turns a basket add of such an article into the price request
     * (the FDI middleware calls FDI's `addPriceRequest` and accepts only 1
     * piece), so this sends `shopBasketAddItem` with `amount: 1`.
     *
     * Failures propagate, so the widget reports success only when the shop
     * confirmed the request.
     */
    async function sendPriceRequestToShop(articleId: string, context?: ShopArticleContext): Promise<void> {
        await addGenericShopBasketItem({ ...buildShopItemInfoInput(articleId, context), amount: 1 });
    }

    /**
     * Navigates to the shop's checkout/basket view (QIT-7076).
     *
     * Same-tab redirect, consistent with FDI/Salesforce/SAP
     * (`window.location.href = ...`) — ShopLight is the one exception, since
     * its cart lives inside InfoTwin. Olaf mentioned the generic checkout
     * might open in a new tab instead (QIT-6923 Gap 10); that's unconfirmed
     * and follows up separately rather than blocking this on an assumption.
     *
     * Resolves even when the URL cannot be resolved — the connector
     * dispatches this on every cart-button click, and a rejection there would
     * only produce a console warning the user never sees. If the user is no
     * longer logged in, the login starts instead.
     */
    async function redirectToBasket(): Promise<void> {
        const url = await loadGenericShopCheckoutUrl();
        if (url) {
            window.location.href = url;
            return;
        }

        await startLoginIfSessionLost();
    }

    return {
        addPartToShopBasket,
        getShopItemInfoForArticle,
        sendPriceRequestToShop,
        getBasket,
        redirectToBasket,
    };
}
